import { z } from 'zod';
/** Matches the Prisma HomecareClientStatus enum (post-B1-reversal: no
 *  PENDING_REVIEW — signup auto-actives). */
export const HomecareClientStatusSchema = z.enum(['ACTIVE', 'SUSPENDED', 'TERMINATED']);
/** Matches the Prisma HomecarePaymentMode enum. STRIPE is the default
 *  per Q9; INVOICE is the fallback for card-averse clients. */
export const HomecarePaymentModeSchema = z.enum(['STRIPE', 'INVOICE']);
/** Matches the Prisma RelationshipToAccountHolder enum. Stored per
 *  Recipient (post-Path-C), not on the account. */
export const RelationshipToAccountHolderSchema = z.enum([
    'SELF',
    'SPOUSE',
    'ADULT_CHILD',
    'PARENT',
    'SIBLING',
    'POA',
    'GUARDIAN',
    'OTHER',
]);
/** POST /api/v1/homecare-clients/signup body.
 *
 *  Public signup form from the marketing site /signup page. Auto-actives
 *  the account; the magic-link sent to the provided email is the
 *  implicit email verification. Recipients are added separately via
 *  the portal after first sign-in.
 *
 *  Anti-spam (same shape as /facilities/inquire):
 *  - `_botField` honeypot — UI hides; humans never fill; bots that
 *    scrape the form usually do. Service drops silently.
 *  - Per-IP rate limit on the controller via @nestjs/throttler.
 */
export const HomecareSignupRequestSchema = z.object({
    // Honeypot
    // audit medium — `.max(200).optional()` (NOT `.max(0)`), matching the working
    // contact/application forms; `.max(0)` made the service honeypot unreachable.
    _botField: z.string().max(200).optional(),
    /** Cloudflare Turnstile token — human-verification; checked by TurnstileGuard.
     *  Optional so dev/e2e (no site key) still submit; enforced when a key is set. */
    turnstileToken: z.string().max(4096).optional(),
    // Account holder identity
    // Audit H16 (2026-07-28): human-readable messages so the public /for-families
    // signup form stops surfacing raw Zod strings ("Invalid email", "String must
    // contain at least 10 character(s)") to a scam-wary 50+ audience.
    email: z.string().email('Please enter a valid email address').max(254).toLowerCase(),
    displayName: z.string().min(1, 'Your name is required').max(120).trim(),
    phone: z.string().min(10, 'Please enter a valid phone number').max(30).trim(),
    // Payment posture — STRIPE-only at signup (v1). Audit 2026-08-16: homecare
    // INVOICE billing has no AR/completion-invoice path, and an INVOICE account
    // would bypass the card gate, then the accept-time pre-auth fails and the
    // booking is SYSTEM-cancelled (a "confirmed-then-vanished" visit) + a wrong
    // "update your card" email. So the public signup MUST NOT create an INVOICE
    // account until a homecare AR path ships. The enum retains INVOICE for
    // existing data + the /me/admin read shapes; this narrows only what signup
    // will accept (a crafted `paymentMode:"INVOICE"` POST now 422s).
    paymentMode: z.literal('STRIPE').optional().default('STRIPE'),
    // Lead context — all optional.
    referralSource: z.string().max(200).trim().nullable().optional(),
});
export const HomecareSignupResponseSchema = z.object({
    ok: z.literal(true),
    /** HomecareClient id on success; null when the honeypot was
     *  triggered (response shape is identical so bots can't probe). */
    reference: z.string().uuid().nullable(),
});
//# sourceMappingURL=signup.schema.js.map